import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/useCart";
import { useConfiguracionTiendaPublic } from "../../hooks/useConfiguracionTiendaPublic";
import {
  obtenerMetodosEnvio,
  actualizarCheckoutMetodoEnvio,
} from "../../../../services/store/Checkout";
import "./CheckoutMetodoEnvio.css";

function CheckoutMetodoEnvio() {
  const navigate = useNavigate();
  const { cart, subtotal, coreChargeTotal, grandTotal, ready } = useCart();
  const { formatMoney } = useConfiguracionTiendaPublic();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Preparando tu checkout..."
  );
  const [error, setError] = useState("");
  const [metodos, setMetodos] = useState([]);
  const [modoCheckout, setModoCheckout] = useState("GLOBAL");
  const [selectedMetodoKey, setSelectedMetodoKey] = useState("");
  const [modalMetodo, setModalMetodo] = useState(null);
  const [form, setForm] = useState({
    envio_empresa: "",
    envio_cuenta: "",
  });
  const [errors, setErrors] = useState({});

  const checkoutDatos = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("checkout_datos") || "{}");
    } catch {
      return {};
    }
  }, []);

  const grupoReserva = useMemo(() => {
    return String(sessionStorage.getItem("grupo_reserva") || "").trim();
  }, []);

  useEffect(() => {
    if (!ready) return;

    if (!Array.isArray(cart) || cart.length === 0) {
      navigate("/store/cart", { replace: true });
    }
  }, [ready, cart, navigate]);

  useEffect(() => {
    if (!checkoutDatos || !grupoReserva) {
      navigate("/store/checkout/datos", { replace: true });
    }
  }, [checkoutDatos, grupoReserva, navigate]);

  useEffect(() => {
    let cancelled = false;

    const loadMetodos = async () => {
      try {
        setLoading(true);
        setLoadingMessage("Consultando métodos de envío disponibles...");
        setError("");

        if (!grupoReserva) {
          if (!cancelled) {
            setMetodos([]);
            setError("No se encontró el grupo de reserva del checkout.");
            setLoading(false);
          }
          return;
        }

        const response = await obtenerMetodosEnvio(grupoReserva);

        const rows = Array.isArray(response?.metodos_envio)
          ? response.metodos_envio
          : [];

        if (!cancelled) {
          setModoCheckout(
            String(response?.modo_pagos_checkout || "GLOBAL").toUpperCase()
          );
          setMetodos(rows);
        }
      } catch (err) {
        console.error("Error cargando métodos de envío:", err);
        if (!cancelled) {
          setMetodos([]);
          setError(
            err?.response?.data?.message ||
              "No se pudieron cargar los métodos de envío."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMetodos();

    return () => {
      cancelled = true;
    };
  }, [grupoReserva]);

  const buildMetodoKey = (metodo) => {
    if (!metodo) return "";
    const metodoId = Number(metodo.tienda_metodo_envio_id || 0);
    const reglaId =
      metodo.tienda_metodo_envio_regla_id == null
        ? "null"
        : Number(metodo.tienda_metodo_envio_regla_id);
    return `${metodoId}:${reglaId}`;
  };

  const selectedMetodo = useMemo(() => {
    return (
      metodos.find((item) => buildMetodoKey(item) === selectedMetodoKey) || null
    );
  }, [metodos, selectedMetodoKey]);

  useEffect(() => {
    setForm({
      envio_empresa: "",
      envio_cuenta: "",
    });
    setErrors({});
  }, [selectedMetodoKey]);

  const getMetodoDisplayName = (metodo) => {
    if (!metodo) return "";
    return String(metodo.nombre_visible || "Método de envío");
  };

  const getMetodoDescripcion = (metodo) => {
    return String(metodo?.descripcion || "").trim();
  };

  const getMetodoZonaNombre = (metodo) => {
    return String(metodo?.zona?.nombre || "General");
  };

  const getMetodoCostoBase = (metodo) => {
    return Number(metodo?.costo_envio?.costo_base || 0);
  };

  const getMetodoRecargo = (metodo) => {
    return Number(metodo?.costo_envio?.recargo || 0);
  };

  const getMetodoTotalEnvio = (metodo) => {
    return Number(metodo?.costo_envio?.total_envio || 0);
  };

  const getMetodoCoberturas = (metodo) => {
    if (!metodo || !metodo.zona) return [];
    return Array.isArray(metodo.zona.coberturas) ? metodo.zona.coberturas : [];
  };

  const getCoberturaTexto = (cobertura) => {
    if (!cobertura) return "Ubicación no disponible";
    return String(cobertura.ubicacion || "").trim() || "Ubicación no disponible";
  };

  const getLineSubtotal = (item) => {
    const precio = Number(item.precio_unitario || item.precio || 0);
    const cantidad = Number(item.cantidad || 0);
    return precio * cantidad;
  };

  const getLineCore = (item) => {
    const core = Number(item.core_charge || 0);
    const cantidad = Number(item.cantidad || 0);
    return core * cantidad;
  };

  const formatTiempoEntrega = (metodo) => {
    if (!metodo) return "Sin tiempo definido";

    const desde = Number(metodo.tiempo_entrega_desde || 0);
    const hasta = Number(metodo.tiempo_entrega_hasta || 0);
    const unidad = String(metodo.unidad_tiempo || "").toUpperCase();

    const unidadSingular = unidad === "HORAS" ? "hora" : "día";
    const unidadPlural = unidad === "HORAS" ? "horas" : "días";

    if (desde > 0 && hasta > 0) {
      return `${desde} - ${hasta} ${
        hasta === 1 ? unidadSingular : unidadPlural
      }`;
    }

    if (desde > 0) {
      return `Desde ${desde} ${desde === 1 ? unidadSingular : unidadPlural}`;
    }

    if (hasta > 0) {
      return `Hasta ${hasta} ${hasta === 1 ? unidadSingular : unidadPlural}`;
    }

    return "Sin tiempo definido";
  };

  const totalEnvio = useMemo(() => {
    if (!selectedMetodo) return 0;
    return getMetodoTotalEnvio(selectedMetodo);
  }, [selectedMetodo]);

  const totalGeneral = useMemo(() => {
    return Number(grandTotal || 0) + Number(totalEnvio || 0);
  }, [grandTotal, totalEnvio]);

  const handleMetodoSelect = (metodo) => {
    setSelectedMetodoKey(buildMetodoKey(metodo));
    setError("");
  };

  const openMetodoModal = (metodo, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setModalMetodo(metodo);
  };

  const closeMetodoModal = () => {
    setModalMetodo(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      if (!prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!selectedMetodoKey) {
      nextErrors.metodo = "Selecciona un método de envío.";
    }

    if (Number(selectedMetodo?.requiere_cuenta) === 1) {
      if (!String(form.envio_empresa || "").trim()) {
        nextErrors.envio_empresa = `${
          selectedMetodo?.label_empresa || "Nombre de empresa"
        } es requerido.`;
      }

      if (!String(form.envio_cuenta || "").trim()) {
        nextErrors.envio_cuenta = `${
          selectedMetodo?.label_cuenta || "Número de cuenta"
        } es requerido.`;
      }
    }

    return nextErrors;
  };

  const handleContinue = async () => {
    const validation = validateForm();
    setErrors(validation);
    setError("");

    if (Object.keys(validation).length > 0) {
      setError("Completa la información de envío para continuar.");
      return;
    }

    try {
      setSubmitting(true);
      setCheckoutSaving(true);
      setLoadingMessage("Guardando método de envío...");

      if (!grupoReserva) {
        throw new Error("No se encontró el grupo de reserva.");
      }

      if (!selectedMetodo) {
        throw new Error("No se ha seleccionado un método de envío.");
      }

      const payloadEnvio = {
        grupo_reserva: grupoReserva,
        tienda_metodo_envio_id: Number(selectedMetodo.tienda_metodo_envio_id),
        tienda_metodo_envio_regla_id:
          selectedMetodo.tienda_metodo_envio_regla_id == null
            ? null
            : Number(selectedMetodo.tienda_metodo_envio_regla_id),
        metodo_envio_compania:
          Number(selectedMetodo.requiere_cuenta || 0) === 1
            ? String(form.envio_empresa || "").trim()
            : null,
        metodo_envio_no_cuenta:
          Number(selectedMetodo.requiere_cuenta || 0) === 1
            ? String(form.envio_cuenta || "").trim()
            : null,

        costo_envio: Number(getMetodoCostoBase(selectedMetodo) || 0),
        recargo_envio: Number(getMetodoRecargo(selectedMetodo) || 0),
        total_envio: Number(getMetodoTotalEnvio(selectedMetodo) || 0),
      };

      const response = await actualizarCheckoutMetodoEnvio(payloadEnvio);

      sessionStorage.setItem("checkout_envio", JSON.stringify(payloadEnvio));

      if (response?.data) {
        sessionStorage.setItem(
          "checkout_reserva_resumen",
          JSON.stringify(response.data)
        );
      }

      navigate(`/store/checkout/pago/${grupoReserva}`);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "No se pudo continuar con el método de envío."
      );
    } finally {
      setSubmitting(false);
      setCheckoutSaving(false);
    }
  };

  const modalCoberturas = useMemo(() => {
    return getMetodoCoberturas(modalMetodo);
  }, [modalMetodo]);

  return (
    <div className="checkout-envio-page">
      <div className="checkout-envio-shell">
        <div className="checkout-envio-topbar">
          <div className="checkout-envio-brand">
            <span className="checkout-envio-brand-dot" />
            Paso 2
          </div>

          <div className="checkout-envio-steps">
            <div className="checkout-envio-step is-done">
              <span>1</span>
              <p>Datos</p>
            </div>
            <div className="checkout-envio-step-line is-active" />
            <div className="checkout-envio-step is-active">
              <span>2</span>
              <p>Envío</p>
            </div>
            <div className="checkout-envio-step-line" />
            <div className="checkout-envio-step">
              <span>3</span>
              <p>Pago</p>
            </div>
          </div>
        </div>

        <div className="checkout-envio-layout">
          <section className="checkout-envio-main">
            <div className="checkout-envio-card checkout-envio-card--hero">
              <div>
                <span className="checkout-envio-badge">Paso 2</span>
                <h1>Método de envío</h1>
                <p>
                  Selecciona cómo deseas recibir tu pedido según la configuración
                  de la tienda.
                </p>
              </div>
            </div>

            <div className="checkout-envio-card">
              <div className="checkout-envio-card-header">
                <div>
                  <h2>Selecciona un método</h2>
                  <p>
                    {modoCheckout === "POR_REGLA_ENVIO"
                      ? "Mostramos los métodos disponibles según la dirección del checkout."
                      : "Mostramos los métodos generales configurados por la tienda."}
                  </p>
                </div>
              </div>

              {loading ? null : metodos.length === 0 ? (
                <div className="checkout-envio-empty">
                  No hay métodos de envío disponibles.
                </div>
              ) : (
                <div className="checkout-envio-methods">
                  {metodos.map((metodo) => {
                    const key = buildMetodoKey(metodo);
                    const isActive = selectedMetodoKey === key;
                    const coberturas = getMetodoCoberturas(metodo);

                    return (
                      <label
                        key={key}
                        className={`checkout-envio-method ${
                          isActive ? "is-selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="metodo_envio"
                          value={key}
                          checked={isActive}
                          onChange={() => handleMetodoSelect(metodo)}
                        />

                        <div className="checkout-envio-method-content">
                          <div className="checkout-envio-method-top">
                            <div>
                              <h3>{getMetodoDisplayName(metodo)}</h3>
                              <p>{formatTiempoEntrega(metodo)}</p>
                            </div>

                            <div className="checkout-envio-method-price">
                              <span>Total envío</span>
                              <strong>
                                {formatMoney(getMetodoTotalEnvio(metodo))}
                              </strong>
                            </div>
                          </div>

                          <div className="checkout-envio-method-cost-detail">
                            <div className="checkout-envio-method-cost-row">
                              <span>Costo base</span>
                              <strong>
                                {formatMoney(getMetodoCostoBase(metodo))}
                              </strong>
                            </div>

                            <div className="checkout-envio-method-cost-row">
                              <span>Recargo</span>
                              <strong>
                                {formatMoney(getMetodoRecargo(metodo))}
                              </strong>
                            </div>

                            <div className="checkout-envio-method-cost-row checkout-envio-method-cost-row--total">
                              <span>Total</span>
                              <strong>
                                {formatMoney(getMetodoTotalEnvio(metodo))}
                              </strong>
                            </div>
                          </div>

                          <div className="checkout-envio-method-meta">
                            <span className="checkout-pill">
                              Zona: {getMetodoZonaNombre(metodo)}
                            </span>

                            {Number(metodo.requiere_cuenta) === 1 && (
                              <span className="checkout-pill checkout-pill--accent">
                                Requiere cuenta
                              </span>
                            )}

                            {coberturas.length > 0 && (
                              <span className="checkout-pill">
                                Coberturas: {coberturas.length}
                              </span>
                            )}
                          </div>

                          <div className="checkout-envio-method-actions">
                            {coberturas.length > 0 && (
                              <button
                                type="button"
                                className="checkout-envio-zona-btn"
                                onClick={(e) => openMetodoModal(metodo, e)}
                              >
                                Ver zonas
                              </button>
                            )}
                          </div>

                          {getMetodoDescripcion(metodo) && (
                            <p className="checkout-envio-method-description">
                              {getMetodoDescripcion(metodo)}
                            </p>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {errors.metodo && (
                <div className="checkout-envio-field-error">
                  {errors.metodo}
                </div>
              )}
            </div>

            {selectedMetodo && Number(selectedMetodo.requiere_cuenta) === 1 && (
              <div className="checkout-envio-card">
                <div className="checkout-envio-card-header">
                  <div>
                    <h2>Datos adicionales</h2>
                    <p>
                      Este método requiere información de cuenta para el envío.
                    </p>
                  </div>
                </div>

                <div className="checkout-envio-form-grid">
                  <div className="checkout-envio-field">
                    <label>
                      {selectedMetodo.label_empresa || "Nombre de empresa"}
                    </label>
                    <input
                      type="text"
                      name="envio_empresa"
                      value={form.envio_empresa}
                      onChange={handleChange}
                      placeholder={
                        selectedMetodo.label_empresa || "Nombre de empresa"
                      }
                      className={errors.envio_empresa ? "is-error" : ""}
                    />
                    {errors.envio_empresa && (
                      <span className="checkout-envio-field-error">
                        {errors.envio_empresa}
                      </span>
                    )}
                  </div>

                  <div className="checkout-envio-field">
                    <label>
                      {selectedMetodo.label_cuenta || "Número de cuenta"}
                    </label>
                    <input
                      type="text"
                      name="envio_cuenta"
                      value={form.envio_cuenta}
                      onChange={handleChange}
                      placeholder={
                        selectedMetodo.label_cuenta || "Número de cuenta"
                      }
                      className={errors.envio_cuenta ? "is-error" : ""}
                    />
                    {errors.envio_cuenta && (
                      <span className="checkout-envio-field-error">
                        {errors.envio_cuenta}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          <aside className="checkout-envio-sidebar">
            <div className="checkout-envio-card checkout-envio-card--sticky">
              <div className="checkout-envio-card-header">
                <div>
                  <h2>Resumen del pedido</h2>
                  <p>Verifica tus productos y el envío antes de continuar.</p>
                </div>
              </div>

              <div className="checkout-envio-products">
                {Array.isArray(cart) && cart.length > 0 ? (
                  cart.map((item) => {
                    const qty = Number(item.cantidad || 0);
                    const lineSubtotal = getLineSubtotal(item);
                    const lineCore = getLineCore(item);

                    return (
                      <div
                        key={item.producto_id || item.carrito_id}
                        className="checkout-envio-product-item"
                      >
                        <div className="checkout-envio-product-top">
                          <div>
                            <strong>{item.nombre}</strong>
                            <span>Cantidad: {qty}</span>
                          </div>

                          <strong>{formatMoney(lineSubtotal)}</strong>
                        </div>

                        {Number(item.core_charge || 0) > 0 && (
                          <div className="checkout-envio-product-core">
                            <span>Core Charge</span>
                            <strong>{formatMoney(lineCore)}</strong>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="checkout-envio-empty">
                    No hay productos en el carrito.
                  </div>
                )}
              </div>

              <div className="checkout-envio-summary">
                <div className="checkout-envio-summary-row">
                  <span>Subtotal productos</span>
                  <strong>{formatMoney(subtotal)}</strong>
                </div>

                {Number(coreChargeTotal) > 0 && (
                  <div className="checkout-envio-summary-row">
                    <span>Core Charge</span>
                    <strong>{formatMoney(coreChargeTotal)}</strong>
                  </div>
                )}

                <div className="checkout-envio-summary-row">
                  <span>Método</span>
                  <strong>
                    {selectedMetodo
                      ? getMetodoDisplayName(selectedMetodo)
                      : "No seleccionado"}
                  </strong>
                </div>

                <div className="checkout-envio-summary-row">
                  <span>Tiempo de entrega</span>
                  <strong>
                    {selectedMetodo
                      ? formatTiempoEntrega(selectedMetodo)
                      : "No definido"}
                  </strong>
                </div>

                <div className="checkout-envio-summary-row">
                  <span>Zona</span>
                  <strong>
                    {selectedMetodo
                      ? getMetodoZonaNombre(selectedMetodo)
                      : "No seleccionada"}
                  </strong>
                </div>

                <div className="checkout-envio-summary-row">
                  <span>Costo envío</span>
                  <strong>{formatMoney(totalEnvio)}</strong>
                </div>

                <div className="checkout-envio-summary-row checkout-envio-summary-row--grand">
                  <span>Total general</span>
                  <strong>{formatMoney(totalGeneral)}</strong>
                </div>
              </div>

              {error && <div className="checkout-envio-alert">{error}</div>}

              <div className="checkout-envio-actions">
                <button
                  type="button"
                  className="checkout-envio-btn checkout-envio-btn--ghost"
                  onClick={() => navigate("/store/checkout/datos")}
                  disabled={submitting || checkoutSaving}
                >
                  Regresar
                </button>

                <button
                  type="button"
                  className="checkout-envio-btn checkout-envio-btn--primary"
                  onClick={handleContinue}
                  disabled={submitting || loading || !ready || checkoutSaving}
                >
                  {checkoutSaving ? "Guardando checkout..." : "Continuar al pago"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {modalMetodo && (
        <div className="checkout-envio-modal-backdrop" onClick={closeMetodoModal}>
          <div
            className="checkout-envio-modal checkout-envio-modal--small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="checkout-envio-modal-header">
              <div>
                <h3>{getMetodoDisplayName(modalMetodo)}</h3>
                <p>Zona y coberturas configuradas para este método de envío.</p>
              </div>

              <button
                type="button"
                className="checkout-envio-modal-close"
                onClick={closeMetodoModal}
              >
                ×
              </button>
            </div>

            <div className="checkout-envio-modal-body">
              <div className="checkout-envio-zona-detalles">
                <div className="checkout-envio-zona-detalle-card checkout-envio-zona-detalle-card--clean">
                  <div className="checkout-envio-zona-card-head">
                    <div>
                      <h4>{modalMetodo?.zona?.nombre || "Zona de envío"}</h4>
                      {modalMetodo?.zona?.descripcion ? (
                        <p>{modalMetodo.zona.descripcion}</p>
                      ) : null}
                    </div>

                    <div className="checkout-envio-zona-card-total">
                      <span>Total envío</span>
                      <strong>
                        {formatMoney(getMetodoTotalEnvio(modalMetodo))}
                      </strong>
                    </div>
                  </div>

                  <div className="checkout-envio-zona-costs">
                    <div>
                      <span>Costo base</span>
                      <strong>
                        {formatMoney(getMetodoCostoBase(modalMetodo))}
                      </strong>
                    </div>

                    <div>
                      <span>Recargo</span>
                      <strong>
                        {formatMoney(getMetodoRecargo(modalMetodo))}
                      </strong>
                    </div>
                  </div>

                  <div className="checkout-envio-zona-coberturas">
                    <span className="checkout-envio-zona-coberturas-title">
                      Coberturas de la zona
                    </span>

                    {modalCoberturas.length > 0 ? (
                      <ul className="checkout-envio-zona-coberturas-list">
                        {modalCoberturas.map((cobertura) => (
                          <li
                            key={cobertura.tienda_zona_envio_detalle_id}
                          >
                            {getCoberturaTexto(cobertura)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="checkout-envio-zona-coberturas-empty">
                        No hay coberturas configuradas para esta zona.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(loading || checkoutSaving) && (
        <div className="checkout-envio-loading-overlay">
          <div className="checkout-envio-loading-card">
            <div className="checkout-envio-loading-track">
              <div className="checkout-envio-loading-cart">
                <span className="checkout-envio-cart-handle" />
                <span className="checkout-envio-cart-basket" />
                <span className="checkout-envio-cart-wheel checkout-envio-cart-wheel--left" />
                <span className="checkout-envio-cart-wheel checkout-envio-cart-wheel--right" />
              </div>
            </div>

            <div className="checkout-envio-loading-spinner">
              <span />
              <span />
              <span />
            </div>

            <h3>
              {checkoutSaving
                ? "Estamos preparando tu pedido"
                : "Buscando opciones de envío"}
            </h3>
            <p>{loadingMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckoutMetodoEnvio;